// @/components/reusable/dropdown-select.jsx
// V2 Dropdown Select - Reusable select/listbox component for forms and charts
// Built on HeadlessUI Listbox with spring animations

import React from 'react';
import * as Headless from '@headlessui/react';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

// ============================================================================
// ANIMATION CONFIG (shared with v2 reusable)
// ============================================================================

const dropdownSpring = {
  type: 'spring',
  damping: 25,
  stiffness: 400,
  mass: 0.4,
};

// ============================================================================
// DROPDOWN SELECT
// ============================================================================

/**
 * DropdownSelect - V2 reusable dropdown select component
 *
 * Features:
 * - Spring animations matching v2 design system
 * - Multiple size variants (sm, md)
 * - Consistent ring/border styles matching Button outlineSmall
 * - Support for disabled options
 * - Checkmark indicator for selected items
 *
 * @param {any} value - Currently selected value
 * @param {Function} onChange - Callback when selection changes
 * @param {Array} options - Array of options: { value, label, disabled? }
 * @param {string} placeholder - Placeholder text when no value selected
 * @param {string} size - Size variant: 'sm' | 'md' (default: 'md')
 * @param {string} className - Additional classes for the container
 * @param {string} buttonClassName - Additional classes for the button
 * @param {boolean} disabled - Whether the select is disabled
 */
export function DropdownSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  size = 'md',
  className,
  buttonClassName,
  disabled = false,
}) {
  // Find the current option's label
  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  // Size-based classes
  const sizeClasses = {
    sm: {
      button: 'px-3.5 py-2 pr-8 text-sm/5',
      icon: 'size-3.5',
      menu: 'text-sm',
      item: 'px-3 py-1.5',
    },
    md: {
      button: 'px-3.5 py-2 pr-9 text-sm/5',
      icon: 'size-4',
      menu: 'text-sm',
      item: 'px-3 py-2',
    },
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  return (
    <AnimatePresence>
      <Headless.Listbox
        value={value}
        onChange={onChange}
        disabled={disabled}
        as="div"
        className={clsx('relative', className)}
      >
        {({ open }) => (
          <>
            <Headless.ListboxButton
              className={clsx(
                // Base layout
                'relative block rounded-lg bg-white cursor-pointer',
                // Typography — match v2 Button
                'font-fono font-medium tracking-tighter text-zinc-900',
                // Sizing
                sizes.button,
                // Ring/border
                'ring-1 ring-inset ring-zinc-300 shadow-xs',
                // Hover states
                'hover:bg-zinc-50 hover:ring-zinc-400',
                // Focus — subtle, no heavy ring
                'focus:outline-hidden',
                // Disabled
                'disabled:opacity-50 disabled:cursor-not-allowed',
                // Transition
                'transition-all duration-150',
                buttonClassName
              )}
            >
              <span className="truncate">{displayLabel}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDownIcon
                  className={clsx(
                    sizes.icon,
                    'text-zinc-500 shrink-0 transition-transform duration-150',
                    open && 'rotate-180'
                  )}
                />
              </span>
            </Headless.ListboxButton>

            <AnimatePresence>
              {open && (
                <Headless.ListboxOptions
                  static
                  as={motion.ul}
                  initial={{ opacity: 0, scale: 0.96, y: -4 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    transition: dropdownSpring,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.96,
                    y: -4,
                    transition: { duration: 0.1 },
                  }}
                  anchor="bottom start"
                  className={clsx(
                    // Positioning
                    '[--anchor-gap:--spacing(1)]',
                    // Base styles
                    'z-200 isolate w-max min-w-(--button-width) rounded-xl p-1.5',
                    // Background
                    'bg-white/95 backdrop-blur-xl',
                    // Shadow and ring
                    'shadow-lg ring-1 ring-black/5',
                    // Max height with scroll
                    'overflow-y-auto max-h-60',
                    // Focus
                    'outline-hidden',
                    sizes.menu
                  )}
                >
                  {options.map((option) => (
                    <Headless.ListboxOption
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      className={clsx(
                        // Base
                        'group flex cursor-pointer items-center justify-between rounded-lg',
                        sizes.item,
                        // Text
                        'text-zinc-800',
                        // Hover/focus states
                        'data-focus:bg-zinc-100 data-focus:text-black',
                        // Selected state
                        'data-selected:font-medium data-selected:text-black',
                        // Disabled
                        'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
                        // Transition
                        'transition-colors duration-100'
                      )}
                    >
                      {({ selected }) => (
                        <>
                          <span className="truncate">{option.label}</span>
                          {selected && (
                            <svg
                              className="size-4 text-black shrink-0 ml-2"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </>
                      )}
                    </Headless.ListboxOption>
                  ))}
                </Headless.ListboxOptions>
              )}
            </AnimatePresence>
          </>
        )}
      </Headless.Listbox>
    </AnimatePresence>
  );
}

// ============================================================================
// DROPDOWN SELECT GROUPED
// ============================================================================

/**
 * DropdownSelectGrouped - V2 dropdown with grouped/sectioned options
 *
 * Groups are rendered with a heading label in primary color and a divider between sections.
 * Based on the Figma design for Geography filter dropdown.
 *
 * @param {any} value - Currently selected value
 * @param {Function} onChange - Callback when selection changes
 * @param {Array} groups - Array of groups: { heading, options: [{ value, label, disabled? }] }
 * @param {string} placeholder - Placeholder text when no value selected
 * @param {string} size - Size variant: 'sm' | 'md' (default: 'md')
 * @param {string} className - Additional classes for the container
 * @param {string} buttonClassName - Additional classes for the button
 * @param {boolean} disabled - Whether the select is disabled
 */
export function DropdownSelectGrouped({
  value,
  onChange,
  groups = [],
  placeholder = 'Select...',
  size = 'md',
  className,
  buttonClassName,
  disabled = false,
}) {
  // Flatten all options to find current selection
  const allOptions = groups.flatMap((g) => g.options);
  const selectedOption = allOptions.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;

  // Size-based classes
  const sizeClasses = {
    sm: {
      button: 'px-3.5 py-2 pr-8 text-sm/5',
      icon: 'size-3.5',
      menu: 'text-sm',
      item: 'px-3 py-1.5',
      heading: 'px-3 py-1.5 text-xs',
    },
    md: {
      button: 'px-3.5 py-2 pr-9 text-sm/5',
      icon: 'size-4',
      menu: 'text-sm',
      item: 'px-3 py-2',
      heading: 'px-3 py-2 text-xs',
    },
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  return (
    <AnimatePresence>
      <Headless.Listbox
        value={value}
        onChange={onChange}
        disabled={disabled}
        as="div"
        className={clsx('relative', className)}
      >
        {({ open }) => (
          <>
            <Headless.ListboxButton
              className={clsx(
                // Base layout
                'relative block rounded-lg bg-white cursor-pointer',
                // Typography — match v2 Button
                'font-fono font-medium tracking-tighter text-zinc-900',
                // Sizing
                sizes.button,
                // Ring/border
                'ring-1 ring-inset ring-zinc-300 shadow-xs',
                // Hover states
                'hover:bg-zinc-50 hover:ring-zinc-400',
                // Focus — subtle, no heavy ring
                'focus:outline-hidden',
                // Disabled
                'disabled:opacity-50 disabled:cursor-not-allowed',
                // Transition
                'transition-all duration-150',
                buttonClassName
              )}
            >
              <span className="truncate">{displayLabel}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDownIcon
                  className={clsx(
                    sizes.icon,
                    'text-zinc-500 shrink-0 transition-transform duration-150',
                    open && 'rotate-180'
                  )}
                />
              </span>
            </Headless.ListboxButton>

            <AnimatePresence>
              {open && (
                <Headless.ListboxOptions
                  static
                  as={motion.div}
                  initial={{ opacity: 0, scale: 0.96, y: -4 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    transition: dropdownSpring,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.96,
                    y: -4,
                    transition: { duration: 0.1 },
                  }}
                  anchor="bottom start"
                  className={clsx(
                    // Positioning
                    '[--anchor-gap:--spacing(1)]',
                    // Base styles
                    'z-200 isolate w-max min-w-(--button-width) rounded-xl p-1.5',
                    // Background - solid white, no blur
                    'bg-white',
                    // Shadow and ring
                    'shadow-lg ring-1 ring-black/5',
                    // Max height with scroll
                    'overflow-y-auto max-h-72',
                    // Focus
                    'outline-hidden',
                    sizes.menu
                  )}
                >
                  {groups.map((group, groupIndex) => (
                    <div key={group.heading}>
                      {/* Divider between groups (not before first) */}
                      {groupIndex > 0 && (
                        <div className="my-1.5 border-t border-zinc-100" />
                      )}

                      {/* Group heading - zinc color to avoid confusion with selected state */}
                      <div className={clsx(sizes.heading, 'font-medium text-zinc-400 select-none')}>
                        {group.heading}
                      </div>

                      {/* Group options */}
                      {group.options.map((option) => (
                        <Headless.ListboxOption
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          className={clsx(
                            // Base
                            'group flex cursor-pointer items-center justify-between rounded-lg',
                            sizes.item,
                            // Text
                            'text-zinc-700',
                            // Hover/focus states
                            'data-focus:bg-zinc-100 data-focus:text-black',
                            // Selected state
                            'data-selected:font-medium data-selected:text-black',
                            // Disabled
                            'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
                            // Transition
                            'transition-colors duration-100'
                          )}
                        >
                          {({ selected }) => (
                            <>
                              <span className="truncate">{option.label}</span>
                              {selected && (
                                <svg
                                  className="size-4 text-black shrink-0 ml-2"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </>
                          )}
                        </Headless.ListboxOption>
                      ))}
                    </div>
                  ))}
                </Headless.ListboxOptions>
              )}
            </AnimatePresence>
          </>
        )}
      </Headless.Listbox>
    </AnimatePresence>
  );
}

export default DropdownSelect;
